/// <reference types="@types/segment-analytics" />

import { AGENCY_VIEW, APP_VIEW, ORG_VIEW } from '../../common/support/viewConstants';
import type { App } from '../../common/types/appTypes';
import type { EntityUserRelation, Org } from '../../common/types/orgTypes';

declare global {
  interface Window {
    analytics: SegmentAnalytics.AnalyticsJS;
  }
}

type SegmentConfig = {
  userId: string;
  email: string;
  name: string;
  isAgencyUser: boolean;
  OnboardingStep?: string;
  currentApp?: App;
  currentOrg?: Org;
  currentAgency?: EntityUserRelation;
  primaryRole?: string;
};

class Segment {
  isSegmentLoaded() {
    return !!window.analytics;
  }

  initialize(config: SegmentConfig) {
    const { userId, email, name, OnboardingStep, currentApp, currentOrg, currentAgency, isAgencyUser, primaryRole } = config;

    if (!this.isSegmentLoaded()) {
      console.warn('Segment not loaded, skipping initialization.');
      return;
    }

    const segmentPayload = {
      userId,
      email,
      name,
      OnboardingStep,
      isAgencyUser,
      role: primaryRole,
    };

    const entityPayload = this.getEntityPayload({
      currentApp,
      currentOrg,
      currentAgency,
    });

    // Identify user
    window.analytics.identify(userId, {
      ...segmentPayload,
      ...entityPayload,
    });

    const getGroupId = (payload: typeof entityPayload): string | undefined => {
      if (!payload) return undefined;
      if ('branchAppId' in payload) return payload.branchAppId;
      if ('branchOrgId' in payload) return payload.branchOrgId;
      if ('branchAgencyId' in payload) return payload.branchAgencyId;
      return undefined;
    };

    if (entityPayload) {
      const groupId = getGroupId(entityPayload);
      const validViews = [ORG_VIEW, AGENCY_VIEW, APP_VIEW];

      if (groupId && validViews.includes(entityPayload.currentView)) {
        window.analytics.group(groupId, entityPayload);
      }
    }
  }

  getEntityPayload({
    currentApp,
    currentOrg,
    currentAgency,
  }: {
    currentApp?: App;
    currentOrg?: Org;
    currentAgency?: EntityUserRelation;
  }) {
    const { current_view: currentView, app_id: branchAppId, organization_id } = window.App.context.view;

    if (!currentView) return null;

    const entityMapping = {
      [APP_VIEW]: {
        branchAppId: `${branchAppId}`,
        appName: currentApp?.name,
        currentView,
      },
      [ORG_VIEW]: {
        branchOrgId: `${organization_id}`,
        orgName: currentOrg?.name,
        currentView,
      },
      [AGENCY_VIEW]: {
        branchAgencyId: `${organization_id}`,
        agencyName: currentAgency?.organization_name,
        currentView,
      },
    };

    return entityMapping[currentView] || null;
  }

  trackEvent(eventName: string, data?: Record<string, any>) {
    if (this.isSegmentLoaded() && eventName) {
      window.analytics.track(eventName, data);
    }
  }

  page(properties?: Record<string, any>) {
    if (this.isSegmentLoaded()) {
      window.analytics.page(properties);
    }
  }
}

export default Segment;
